import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { Loader2, ArrowLeft, CheckCircle2, PlayCircle, FileText, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import DOMPurify from "isomorphic-dompurify";
import type { Tables } from "@/integrations/supabase/types";

type LessonWithCourse = Tables<"lessons"> & {
  courses?: { id: string; name: string } | null;
};

export const Route = createFileRoute("/_authenticated/student/lesson/$lessonId")({
  component: LessonViewer,
});

function LessonViewer() {
  const { lessonId } = Route.useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const router = useRouter();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lesson, setLesson] = useState<LessonWithCourse | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    if (!user || !lessonId) return;

    async function loadLesson() {
      try {
        // Fetch lesson with course details
        const { data: lessonData, error: lessonError } = await supabase
          .from("lessons")
          .select("*, courses(id, name)")
          .eq("id", lessonId)
          .single();

        if (lessonError || !lessonData) throw new Error("الدرس غير موجود.");

        // Check enrollment
        const courseId = lessonData.courses?.id;
        if (!courseId) throw new Error("خطأ في بيانات الكورس.");

        const { data: memberCheck } = await supabase
          .from("group_members")
          .select("id, groups!inner(course_id)")
          .eq("student_id", user?.id || "")
          .eq("groups.course_id", courseId)
          .limit(1);

        if (!memberCheck || memberCheck.length === 0) {
          throw new Error("عفواً، أنت غير مسجل في هذا الكورس لتشاهد هذا الدرس.");
        }

        // Check if completed
        const { data: progressCheck } = await supabase
          .from("lesson_progress")
          .select("id")
          .eq("student_id", user?.id || "")
          .eq("lesson_id", lessonId)
          .limit(1);

        setLesson(lessonData);
        setIsCompleted((progressCheck ?? []).length > 0);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "حدث خطأ ما");
      } finally {
        setLoading(false);
      }
    }

    loadLesson();
  }, [lessonId, user]);

  const markComplete = async () => {
    if (!user || !lesson || isCompleted) return;
    setCompleting(true);
    try {
      const res = await import("@/lib/lesson.functions").then((m) =>
        m.markLessonCompleteFn({ data: { lessonId: lesson.id } }),
      );

      setIsCompleted(true);

      if (res?.pointsRewarded && res.pointsRewarded > 0) {
        // Just invalidate/refetch profile points locally or display a toast
        import("sonner").then((m) => m.toast.success(`You earned ${res.pointsRewarded} points!`));
      }
    } catch (err: unknown) {
      console.error("Error marking complete:", err);
      const msg = err instanceof Error ? err.message : "Failed to mark complete";
      import("sonner").then((m) => m.toast.error(msg));
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !lesson) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-2xl text-center">
        <div className="bg-destructive/10 text-destructive p-8 rounded-3xl mb-6 flex flex-col items-center">
          <Lock className="h-12 w-12 mb-4" />
          <h2 className="text-2xl font-bold mb-2">غير مصرح</h2>
          <p>{error}</p>
        </div>
        <Button onClick={() => router.history.back()} variant="outline" className="rounded-xl">
          <ArrowLeft className="me-2 h-4 w-4" /> العودة
        </Button>
      </div>
    );
  }

  // Convert YouTube URL to embed format if needed
  const getEmbedUrl = (url: string) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    if (match && match[2].length === 11) {
      return `https://www.youtube.com/embed/${match[2]}`;
    }
    return url;
  };

  const embedUrl = lesson.video_url ? getEmbedUrl(lesson.video_url) : null;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-4 mb-4">
        <Button
          onClick={() => router.history.back()}
          variant="ghost"
          size="icon"
          className="rounded-full"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl lg:text-3xl font-extrabold flex items-center gap-3">
            {lesson.title}
          </h1>
          <p className="text-muted-foreground mt-1">كورس: {lesson.courses?.name}</p>
        </div>
        <div className="ms-auto">
          {isCompleted ? (
            <Badge className="bg-success text-success-foreground px-3 py-1.5 rounded-full flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4" /> مكتمل
            </Badge>
          ) : (
            <Badge
              variant="secondary"
              className="px-3 py-1.5 rounded-full text-muted-foreground flex items-center gap-1.5"
            >
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" /> قيد المتابعة
            </Badge>
          )}
        </div>
      </div>

      {embedUrl && (
        <Card className="rounded-3xl border-0 shadow-soft overflow-hidden bg-black/5">
          <div className="aspect-video w-full bg-black relative rounded-t-3xl overflow-hidden">
            <iframe
              src={embedUrl}
              title={lesson.title}
              className="absolute top-0 left-0 w-full h-full"
              allowFullScreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            />
          </div>
          <CardContent className="p-4 bg-surface flex justify-between items-center">
            <div className="flex items-center gap-2 text-sm font-bold">
              <PlayCircle className="h-5 w-5 text-primary" /> فيديو الدرس
            </div>
          </CardContent>
        </Card>
      )}

      {lesson.content && (
        <Card className="rounded-3xl border-0 shadow-soft">
          <CardContent className="p-6 md:p-8">
            <h2 className="text-xl font-bold flex items-center gap-2 mb-4 border-b border-border pb-2">
              <FileText className="h-5 w-5 text-primary" /> محتوى الدرس
            </h2>
            <div
              className="prose prose-invert prose-p:text-foreground/80 prose-headings:text-foreground max-w-none"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(lesson.content || "") }}
            />
          </CardContent>
        </Card>
      )}

      <div className="flex justify-between items-center py-6 border-t border-border mt-8">
        <Button
          variant="outline"
          size="lg"
          className="rounded-2xl"
          onClick={() => router.history.back()}
        >
          الرجوع للقائمة
        </Button>
        <Button
          size="lg"
          className="rounded-2xl font-bold text-lg px-8 bg-gradient-primary border-0"
          onClick={markComplete}
          disabled={isCompleted || completing}
        >
          {completing ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : isCompleted ? (
            "انتهيت منه بالفعل 🎉"
          ) : (
            "تحديد كمكتمل وكسب نقاط! ⭐"
          )}
        </Button>
      </div>
    </div>
  );
}
